import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useFirebase } from '../context/FirebaseContext';
import { useNavigate } from 'react-router-dom';
import { FaImage, FaHome, FaChurch, FaCalendarAlt, FaBlog, FaBible, FaHandshake, FaChartBar } from 'react-icons/fa';

const BackendManager = () => {
  const [activeSection, setActiveSection] = useState('welcome');
  const [formData, setFormData] = useState({});
  const [message, setMessage] = useState('');
  const [sectionData, setSectionData] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const { 
    createDocument, 
    getAllDocuments, 
    updateDocument, 
    deleteDocument, 
    loading: firebaseLoading, 
    signOut,
    user 
  } = useFirebase();
  const navigate = useNavigate();

  const menuItems = useMemo(() => [
    { id: "banner", label: "Banner", icon: <FaImage /> },
    { id: "welcome", label: "Welcome", icon: <FaHome /> },
    { id: "aboutus", label: "About Us", icon: <FaHome /> },
    { id: "services", label: "Services", icon: <FaChurch /> },
    { id: "servicesHeader", label: "Services Header", icon: <FaChurch /> },
    { id: "sermonsHeader", label: "Evangelism Header", icon: <FaBible /> },
    { id: "events", label: "Events", icon: <FaCalendarAlt /> },
    { id: "blog", label: "Blog", icon: <FaBlog /> },
    { id: "sermons", label: "Sermons", icon: <FaBible /> },
    { id: "partners", label: "Partners", icon: <FaHandshake /> },
    { id: "statistics", label: "Statistics", icon: <FaChartBar /> },
  ], []);

  // Check authentication
  useEffect(() => {
    if (!user) {
      console.log('No user found, redirecting to login');
      navigate('/admin/login');
    }
  }, [user, navigate]);

  const loadSectionData = useCallback(async () => {
    try {
      const currentSection = menuItems.find(s => s.id === activeSection);
      if (!currentSection) {
        console.error('No section found for:', activeSection);
        setMessage('Error: Invalid section');
        return;
      }
      console.log('Loading data for section:', currentSection.id);
      const data = await getAllDocuments(currentSection.id);
      console.log('Loaded section data:', data);
      setSectionData(data || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setMessage('Error loading data: ' + err.message);
    }
  }, [activeSection, getAllDocuments, menuItems]);

  // Load section data when activeSection changes
  useEffect(() => {
    if (user) {
      loadSectionData();
    }
  }, [user, loadSectionData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    
    try {
      const currentSection = menuItems.find(s => s.id === activeSection);
      
      if (editingItem) {
        // Update existing document
        console.log('Updating document:', editingItem.id, { formData });
        
        // Only include fields that have values
        const cleanFormData = {};
        Object.entries(formData).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            cleanFormData[key] = value;
          }
        });

        await updateDocument(currentSection.id, editingItem.id, cleanFormData);
        setMessage('Content updated successfully!');
        setEditingItem(null);
      } else {
        // Create new document
        console.log('Creating new document:', { formData });
        const docId = await createDocument(currentSection.id, formData);
        console.log('Document created with ID:', docId);
        setMessage('Content created successfully!');
      }

      setFormData({});
      await loadSectionData();
    } catch (err) {
      console.error('Error saving content:', err);
      setMessage('Error saving content: ' + err.message);
    }
  };

  const handleEdit = (item) => {
    // Create a clean copy of the item data
    const editData = { ...item };
    
    // Remove internal fields
    delete editData.id;
    delete editData.createdAt;
    delete editData.updatedAt;
    
    setEditingItem(item);
    setFormData(editData);
    
    // Scroll to the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setFormData({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const currentSection = menuItems.find(s => s.id === activeSection);
      await deleteDocument(currentSection.id, id);
      setMessage('Item deleted successfully!');
      loadSectionData();
    } catch (err) {
      setMessage('Error deleting item: ' + err.message);
    }
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const renderItemDetails = (item) => {
    switch (activeSection) {
      case 'banner':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Church Name</p>
                <p className="text-gray-900">{item.churchName}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Main Text</p>
                <p className="text-gray-900">{item.mainText}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Highlighted Text</p>
                <p className="text-gray-900">{item.highlightedText}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Sub Text</p>
                <p className="text-gray-900">{item.subText}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Bottom Text</p>
                <p className="text-gray-900">{item.bottomText}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-500">Description</p>
                <p className="text-gray-600 whitespace-pre-wrap">{item.description}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Primary Button Text</p>
                <p className="text-gray-900">{item.primaryButtonText}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Secondary Button Text</p>
                <p className="text-gray-900">{item.secondaryButtonText}</p>
              </div>
            </div>
          </div>
        );

      case 'welcome':
        return (
          <div className="space-y-3">
            <h4 className="font-medium text-lg text-gray-900">{item.title}</h4>
            <p className="text-gray-600 whitespace-pre-wrap">{item.content}</p>
          </div>
        );

      case 'services':
        return (
          <div className="space-y-3">
            <h4 className="font-medium text-lg text-gray-900">{item.name}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Location</p>
                <p className="text-gray-900">{item.location}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Sunday Service Times</p>
                <p className="text-gray-900">{item.sundayTimes}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-500">Weekday Service Times</p>
                <p className="text-gray-900 whitespace-pre-wrap">{item.weekdayTimes}</p>
              </div>
            </div>
          </div>
        );

      case 'sermons':
        return (
          <div className="space-y-3">
            <h4 className="font-medium text-lg text-gray-900">{item.title}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Pastor</p>
                <p className="text-gray-900">{item.pastor}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-500">Description</p>
                <p className="text-gray-600 whitespace-pre-wrap">{item.description}</p>
              </div>
            </div>
          </div>
        );

      case 'events':
        return (
          <div className="space-y-3">
            <h4 className="font-medium text-lg text-gray-900">{item.eventTitle}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Start Time</p>
                <p className="text-gray-900">{new Date(item.startTime).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">End Time</p>
                <p className="text-gray-900">{new Date(item.endTime).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Pastor</p>
                <p className="text-gray-900">{item.pastorName}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-500">Description</p>
                <p className="text-gray-600 whitespace-pre-wrap">{item.eventDescription}</p>
              </div>
            </div>
          </div>
        );

      case 'blog':
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-lg text-gray-900">{item.title}</h4>
              <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                {item.category}
              </span>
            </div>
            <p className="text-gray-600 whitespace-pre-wrap">{item.description}</p>
          </div>
        );

      case 'gallery':
        return (
          <div className="space-y-3">
            <h4 className="font-medium text-lg text-gray-900">{item.title}</h4>
            <p className="text-gray-600 whitespace-pre-wrap">{item.description}</p>
          </div>
        );

      case 'contact':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Address</p>
                <p className="text-gray-900">{item.address}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Email</p>
                <a href={`mailto:${item.email}`} className="text-blue-600 hover:text-blue-800">
                  {item.email}
                </a>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Phone Numbers</p>
                <p className="text-gray-900">{item.phones}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Website</p>
                <a href={item.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                  {item.website}
                </a>
              </div>
            </div>
          </div>
        );

      case 'partners':
        return (
          <div className="space-y-3">
            <h4 className="font-medium text-lg text-gray-900">{item.name}</h4>
            <p className="text-gray-600 whitespace-pre-wrap">{item.description}</p>
            {item.websiteUrl && (
              <div>
                <a href={item.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Visit Website
                </a>
              </div>
            )}
          </div>
        );

      case 'postal':
        return (
          <div className="space-y-3">
            <h4 className="font-medium text-lg text-gray-900">{item.title}</h4>
            <p className="text-gray-600 whitespace-pre-wrap">{item.description}</p>
            {item.capabilities && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">Capabilities</p>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  {item.capabilities.split('\n').map((capability, index) => (
                    <li key={index}>{capability}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 'servicesHeader':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Title</p>
                <p className="text-gray-900">{item.title}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Subtitle</p>
                <p className="text-gray-900">{item.subtitle}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-500">Description</p>
                <p className="text-gray-600 whitespace-pre-wrap">{item.description}</p>
              </div>
            </div>
          </div>
        );

      case 'statistics':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Title</p>
                <p className="text-gray-900">{item.title}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Number</p>
                <p className="text-gray-900">{item.number}</p>
              </div>
            </div>
          </div>
        );

      case 'sermonsHeader':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Title</p>
                <p className="text-gray-900">{item.title}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Subtitle</p>
                <p className="text-gray-900">{item.subtitle}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-500">Description</p>
                <p className="text-gray-600 whitespace-pre-wrap">{item.description}</p>
              </div>
            </div>
          </div>
        );

      case 'aboutus':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Title</p>
                <p className="text-gray-900">{item.title}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-500">Description</p>
                <p className="text-gray-600 whitespace-pre-wrap">{item.description}</p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderForm = () => {
    switch (activeSection) {
      case 'welcome':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title || ''}
                onChange={(e) => handleInputChange("title", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Content</label>
              <textarea
                name="content"
                value={formData.content || ''}
                onChange={(e) => handleInputChange("content", e.target.value)}
                rows="4"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        );

      case 'services':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Service Name</label>
              <input
                type="text"
                name="name"
                value={formData.name || ''}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Location</label>
              <input
                type="text"
                name="location"
                value={formData.location || ''}
                onChange={(e) => handleInputChange("location", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Weekday Service Times</label>
              <textarea
                name="weekdayTimes"
                value={formData.weekdayTimes || ''}
                onChange={(e) => handleInputChange("weekdayTimes", e.target.value)}
                rows="2"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="e.g., Tuesday from 5:00 am to 7:00 pm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Sunday Service Times</label>
              <input
                type="text"
                name="sundayTimes"
                value={formData.sundayTimes || ''}
                onChange={(e) => handleInputChange("sundayTimes", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="e.g., 8:00 am to 12:00 o'clock"
              />
            </div>
          </div>
        );

      case 'sermons':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title || ''}
                onChange={(e) => handleInputChange("title", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Pastor</label>
              <input
                type="text"
                name="pastor"
                value={formData.pastor || ''}
                onChange={(e) => handleInputChange("pastor", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={(e) => handleInputChange("description", e.target.value)}
                rows="3"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        );

      case 'events':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Event Title</label>
              <input
                type="text"
                name="eventTitle"
                value={formData.eventTitle || ''}
                onChange={(e) => handleInputChange("eventTitle", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Start Time</label>
              <input
                type="datetime-local"
                name="startTime"
                value={formData.startTime || ''}
                onChange={(e) => handleInputChange("startTime", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">End Time</label>
              <input
                type="datetime-local"
                name="endTime"
                value={formData.endTime || ''}
                onChange={(e) => handleInputChange("endTime", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Pastor Name</label>
              <input
                type="text"
                name="pastorName"
                value={formData.pastorName || ''}
                onChange={(e) => handleInputChange("pastorName", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Event Description</label>
              <textarea
                name="eventDescription"
                value={formData.eventDescription || ''}
                onChange={(e) => handleInputChange("eventDescription", e.target.value)}
                rows="3"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        );

      case 'blog':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title || ''}
                onChange={(e) => handleInputChange("title", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={(e) => handleInputChange("description", e.target.value)}
                rows="4"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <select
                name="category"
                value={formData.category || ''}
                onChange={(e) => handleInputChange("category", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Select a category</option>
                <option value="pastoral">Discipleship for Development (D4D)</option>
                <option value="secondary">Secondary Schools</option>
                <option value="primary">Primary Schools</option>
                <option value="nursery">Nursery Schools</option>
                <option value="esk">Ecole Secondaire de Kidaho (ESK)</option>
                <option value="fsk">Friends School of Kamembe (FSK)</option>
                <option value="cgfb">College George Fox de Butaro (CGFB)</option>
                <option value="cgfk">College George Fox de Kagarama (CGFK)</option>
                <option value="faet">Forward Africa Education Trust (FAET)</option>
                <option value="hipp">HIPP Rwanda</option>
                <option value="peacebuilding">EEAR Peacebuilding Initiatives</option>
                <option value="fph">Friends Peace House</option>
                <option value="compassion">Projects Supported by Compassion International</option>
              </select>
            </div>
          </div>
        );

      case 'gallery':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title || ''}
                onChange={(e) => handleInputChange("title", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={(e) => handleInputChange("description", e.target.value)}
                rows="2"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        );

      case 'contact':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Address</label>
              <input
                type="text"
                name="address"
                value={formData.address || ''}
                onChange={(e) => handleInputChange("address", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="e.g., KK15 Rd, 71 St Kigali, Rwanda"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone Numbers (comma-separated)</label>
              <input
                type="text"
                name="phones"
                value={formData.phones || ''}
                onChange={(e) => handleInputChange("phones", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="e.g., +250 788849548, +250 788844385"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email || ''}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="e.g., eearwandaym@gmail.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Website</label>
              <input
                type="text"
                name="website"
                value={formData.website || ''}
                onChange={(e) => handleInputChange("website", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="e.g., www.evangelicalfriendschurchofrwanda.com"
              />
            </div>
          </div>
        );

      case 'partners':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Partner Name</label>
              <input
                type="text"
                name="name"
                value={formData.name || ''}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={(e) => handleInputChange("description", e.target.value)}
                rows="3"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Website URL</label>
              <input
                type="text"
                name="websiteUrl"
                value={formData.websiteUrl || ''}
                onChange={(e) => handleInputChange("websiteUrl", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        );

      case 'postal':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title || ''}
                onChange={(e) => handleInputChange("title", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={(e) => handleInputChange("description", e.target.value)}
                rows="4"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Capabilities (one per line)</label>
              <textarea
                name="capabilities"
                value={formData.capabilities || ''}
                onChange={(e) => handleInputChange("capabilities", e.target.value)}
                rows="3"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="The power to live&#10;The power to do&#10;The power to think"
              />
            </div>
          </div>
        );

      case 'banner':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Church Name</label>
              <input
                type="text"
                name="churchName"
                value={formData.churchName || ""}
                onChange={(e) => handleInputChange("churchName", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter church name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Main Text</label>
              <input
                type="text"
                name="mainText"
                value={formData.mainText || ""}
                onChange={(e) => handleInputChange("mainText", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter main text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Highlighted Text</label>
              <input
                type="text"
                name="highlightedText"
                value={formData.highlightedText || ""}
                onChange={(e) => handleInputChange("highlightedText", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter highlighted text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Sub Text</label>
              <input
                type="text"
                name="subText"
                value={formData.subText || ""}
                onChange={(e) => handleInputChange("subText", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter sub text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Bottom Text</label>
              <input
                type="text"
                name="bottomText"
                value={formData.bottomText || ""}
                onChange={(e) => handleInputChange("bottomText", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter bottom text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={formData.description || ""}
                onChange={(e) => handleInputChange("description", e.target.value)}
                rows={6}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Primary Button Text</label>
              <input
                type="text"
                name="primaryButtonText"
                value={formData.primaryButtonText || ""}
                onChange={(e) => handleInputChange("primaryButtonText", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter primary button text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Secondary Button Text</label>
              <input
                type="text"
                name="secondaryButtonText"
                value={formData.secondaryButtonText || ""}
                onChange={(e) => handleInputChange("secondaryButtonText", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter secondary button text"
              />
            </div>
          </div>
        );

      case 'statistics':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title || ''}
                onChange={(e) => handleInputChange("title", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter statistic title (e.g., MEMBERS, PASTORS)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Number</label>
              <input
                type="text"
                name="number"
                value={formData.number || ''}
                onChange={(e) => handleInputChange("number", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter statistic number (e.g., 7000, 51, 10+)"
              />
            </div>
          </div>
        );

      case 'servicesHeader':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title || ''}
                onChange={(e) => handleInputChange("title", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter title (e.g., S E R V I C E S)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Subtitle</label>
              <input
                type="text"
                name="subtitle"
                value={formData.subtitle || ''}
                onChange={(e) => handleInputChange("subtitle", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter subtitle (e.g., Eglise Evangelique des Amis au Rwanda (EEAR))"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={(e) => handleInputChange("description", e.target.value)}
                rows="3"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter description"
              />
            </div>
          </div>
        );

      case 'sermonsHeader':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title || ''}
                onChange={(e) => handleInputChange("title", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter title (e.g., E V A N G E L I S M)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Subtitle</label>
              <input
                type="text"
                name="subtitle"
                value={formData.subtitle || ''}
                onChange={(e) => handleInputChange("subtitle", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter subtitle (e.g., EVANGELISM)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={(e) => handleInputChange("description", e.target.value)}
                rows="4"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter description"
              />
            </div>
          </div>
        );

      case 'aboutus':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <select
                name="title"
                value={formData.title || ''}
                onChange={(e) => handleInputChange("title", e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
              >
                <option value="">Select a section</option>
                <option value="VISION">VISION</option>
                <option value="MISSION">MISSION</option>
                <option value="GOALS">GOALS</option>
                <option value="VALUES">VALUES</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={(e) => handleInputChange("description", e.target.value)}
                rows="6"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Enter description"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/admin/login');
    } catch (error) {
      setMessage('Error signing out: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8 bg-white rounded-2xl shadow-lg p-6 transform hover:scale-[1.01] transition-all duration-200">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-xl">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              Backend Management
            </h1>
            <button
              onClick={handleLogout}
              disabled={firebaseLoading}
              className="flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
            >
              {firebaseLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Signing out...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Logout</span>
                </>
              )}
            </button>
          </div>
          
          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-4">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <nav className="space-y-2 bg-white rounded-2xl shadow-lg p-4 sticky top-8">
                {menuItems.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => {
                      if (editingItem) {
                        if (window.confirm('Are you sure you want to switch sections? Any unsaved changes will be lost.')) {
                          handleCancelEdit();
                          setActiveSection(section.id);
                        }
                      } else {
                        setActiveSection(section.id);
                      }
                    }}
                    className={`w-full text-left px-6 py-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                      activeSection === section.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md transform scale-[1.02]'
                        : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                    }`}
                  >
                    {section.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Main content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-3">
                      {editingItem ? (
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </div>
                      ) : (
                        <div className="p-2 bg-green-100 rounded-lg">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </div>
                      )}
                      {editingItem ? 'Edit ' : 'Add New '}
                      {menuItems.find(s => s.id === activeSection)?.label}
                    </h2>
                    {editingItem && (
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="text-gray-600 hover:text-gray-800 transition-colors duration-200 flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Cancel Edit
                      </button>
                    )}
                  </div>
                </div>

                <div className="p-8">
                  {message && (
                    <div className={`p-4 mb-6 rounded-xl ${
                      message.includes('Error')
                        ? 'bg-red-50 text-red-700 border-2 border-red-200'
                        : 'bg-green-50 text-green-700 border-2 border-green-200'
                    } animate-fadeIn`}>
                      <div className="flex items-center gap-3">
                        {message.includes('Error') ? (
                          <div className="p-2 bg-red-100 rounded-lg">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                        ) : (
                          <div className="p-2 bg-green-100 rounded-lg">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                        )}
                        {message}
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-8">
                    <div className="bg-gray-50 rounded-xl p-6">
                      {renderForm()}
                    </div>
                    
                    <div className="flex justify-end space-x-4 pt-6 border-t">
                      {editingItem && (
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-sm hover:shadow"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        type="submit"
                        disabled={firebaseLoading}
                        className="px-6 py-3 border-2 border-transparent rounded-xl text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center gap-2"
                      >
                        {firebaseLoading ? (
                          <>
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>{editingItem ? 'Update' : 'Save'}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>

                  {/* Display existing items */}
                  <div className="mt-12 border-t pt-8">
                    <h3 className="text-lg font-medium text-gray-900 mb-8 flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      Existing Items
                    </h3>
                    <div className="space-y-6">
                      {sectionData.map((item) => (
                        <div key={item.id} className="bg-white rounded-xl border-2 border-gray-100 shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-[1.01]">
                          <div className="p-6">
                            <div className="flex-1">
                              {renderItemDetails(item)}
                            </div>
                            <div className="mt-6 flex justify-end space-x-4 pt-4 border-t">
                              <button
                                type="button"
                                onClick={() => handleEdit(item)}
                                className="text-blue-600 hover:text-blue-800 transition-colors duration-200 flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-blue-50"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to delete this item?')) {
                                    handleDelete(item.id);
                                  }
                                }}
                                className="text-red-600 hover:text-red-800 transition-colors duration-200 flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-red-50"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackendManager;